import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useApplicationData } from './state/useApplicationData';
import { useOverlays } from './state/useOverlays';
import { usePageState } from './state/usePageState';
function useApplicationState() {
  const { isAuthenticated, loading: authLoading, user: authUser, logout, isAdmin } = useAuth();
  const page = usePageState();
  const resetNavigation = page.navigation.reset;
  const previousUser = useRef<number | null>();
  useEffect(() => {
    if (authLoading) return;
    const currentUser = isAuthenticated ? (authUser?.id ?? null) : null;
    if (typeof previousUser.current === 'number' && previousUser.current !== currentUser)
      resetNavigation();
    previousUser.current = currentUser;
  }, [authLoading, isAuthenticated, authUser?.id, resetNavigation]);
  const data = useApplicationData(isAuthenticated, authUser?.id);
  const overlays = useOverlays(isAuthenticated);
  return {
    isAuthenticated,
    authLoading,
    authUser,
    logout,
    isAdmin,
    ...page,
    ...data,
    ...overlays,
    handlePersonSelect: page.setSelectedPersonId,
    handleBackToList: () => page.setSelectedPersonId(null),
    handlePersonCreated: data.loadPeople,
    handlePersonDeleted: () => {
      page.setSelectedPersonId(null);
      data.loadPeople();
    },
    handleConfigChange: () => {
      data.loadPeople();
      data.loadGameStats();
    },
  };
}
export type ApplicationState = ReturnType<typeof useApplicationState>;
const ApplicationContext = createContext<ApplicationState | null>(null);
export function ApplicationProvider({ children }: React.PropsWithChildren) {
  const value = useApplicationState();
  return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>;
}
export function useApplication() {
  const value = useContext(ApplicationContext);
  if (!value) throw new Error('ApplicationProvider is required');
  return value;
}
